import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const DeliveryContext = createContext();

export const useDelivery = () => {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error('useDelivery must be used within DeliveryProvider');
  }
  return context;
};

// Delivery boy status constants
export const DELIVERY_STATUS = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  OFFLINE: 'offline'
};

export const DeliveryProvider = ({ children }) => {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);

  // Subscribe to delivery boys in real-time
  useEffect(() => {
    const q = query(
      collection(db, 'delivery_boys'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const deliveryBoysList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDeliveryBoys(deliveryBoysList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching delivery boys:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to active deliveries (orders that are out for delivery)
  useEffect(() => {
    const q = query(
      collection(db, 'orders'),
      where('status', '==', 'out_for_delivery'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const deliveriesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActiveDeliveries(deliveriesList);
      setDeliveriesLoading(false);
    }, (error) => {
      console.error('Error fetching active deliveries:', error);
      setDeliveriesLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add new delivery boy
  const addDeliveryBoy = async (deliveryBoyData) => {
    try {
      const newDeliveryBoy = {
        ...deliveryBoyData,
        status: DELIVERY_STATUS.OFFLINE,
        currentOrderId: null,
        currentLocation: null,
        totalDeliveries: 0,
        totalEarnings: 0,
        rating: 0,
        ratingCount: 0,
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'delivery_boys'), newDeliveryBoy);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding delivery boy:', error);
      return { success: false, error };
    }
  };

  // Update delivery boy
  const updateDeliveryBoy = async (deliveryBoyId, updates) => {
    try {
      const deliveryBoyRef = doc(db, 'delivery_boys', deliveryBoyId);
      await updateDoc(deliveryBoyRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating delivery boy:', error);
      return { success: false, error };
    }
  };

  // Delete delivery boy
  const deleteDeliveryBoy = async (deliveryBoyId) => {
    try {
      await deleteDoc(doc(db, 'delivery_boys', deliveryBoyId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting delivery boy:', error);
      return { success: false, error };
    }
  };

  // Toggle delivery boy active status
  const toggleDeliveryBoyStatus = async (deliveryBoyId, currentStatus) => {
    return updateDeliveryBoy(deliveryBoyId, { isActive: !currentStatus });
  };

  // Update delivery boy online/offline status
  const updateOnlineStatus = async (deliveryBoyId, status) => {
    return updateDeliveryBoy(deliveryBoyId, { status });
  };

  // Update delivery boy location
  const updateLocation = async (deliveryBoyId, location) => {
    return updateDeliveryBoy(deliveryBoyId, {
      currentLocation: {
        ...location,
        updatedAt: Timestamp.now()
      }
    });
  };

  // Assign order to delivery boy
  const assignOrder = async (deliveryBoyId, orderId) => {
    try {
      // Update delivery boy with current order
      await updateDeliveryBoy(deliveryBoyId, {
        currentOrderId: orderId,
        status: DELIVERY_STATUS.BUSY
      });

      return { success: true };
    } catch (error) {
      console.error('Error assigning order:', error);
      return { success: false, error };
    }
  };

  // Complete delivery
  const completeDelivery = async (deliveryBoyId, earnings = 0) => {
    try {
      const deliveryBoy = deliveryBoys.find(db => db.id === deliveryBoyId);
      if (!deliveryBoy) {
        return { success: false, error: 'Delivery boy not found' };
      }

      await updateDeliveryBoy(deliveryBoyId, {
        currentOrderId: null,
        status: DELIVERY_STATUS.AVAILABLE,
        totalDeliveries: (deliveryBoy.totalDeliveries || 0) + 1,
        totalEarnings: (deliveryBoy.totalEarnings || 0) + earnings
      });

      return { success: true };
    } catch (error) {
      console.error('Error completing delivery:', error);
      return { success: false, error };
    }
  };

  // Update delivery boy rating
  const updateRating = async (deliveryBoyId, newRating) => {
    try {
      const deliveryBoy = deliveryBoys.find(db => db.id === deliveryBoyId);
      if (!deliveryBoy) {
        return { success: false, error: 'Delivery boy not found' };
      }

      const currentRating = deliveryBoy.rating || 0;
      const ratingCount = deliveryBoy.ratingCount || 0;
      const updatedRating = ((currentRating * ratingCount) + newRating) / (ratingCount + 1);

      await updateDeliveryBoy(deliveryBoyId, {
        rating: Math.round(updatedRating * 10) / 10,
        ratingCount: ratingCount + 1
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating rating:', error);
      return { success: false, error };
    }
  };

  // Get delivery boys by status
  const getDeliveryBoysByStatus = (status) => {
    return deliveryBoys.filter(db => db.status === status && db.isActive);
  };

  // Get available delivery boys
  const getAvailableDeliveryBoys = () => {
    return deliveryBoys.filter(db => db.status === DELIVERY_STATUS.AVAILABLE && db.isActive);
  };

  // Get active (online) delivery boys
  const getActiveDeliveryBoys = () => {
    return deliveryBoys.filter(db =>
      (db.status === DELIVERY_STATUS.AVAILABLE || db.status === DELIVERY_STATUS.BUSY) &&
      db.isActive
    );
  };

  // Get delivery boy by ID
  const getDeliveryBoyById = (id) => {
    return deliveryBoys.find(db => db.id === id);
  };

  // Get deliveries by delivery boy ID
  const getDeliveriesByDeliveryBoy = (deliveryBoyId) => {
    return activeDeliveries.filter(d => d.deliveryBoy?.id === deliveryBoyId);
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const active = deliveryBoys.filter(db => db.isActive);
    const online = active.filter(db => db.status !== DELIVERY_STATUS.OFFLINE);
    const available = active.filter(db => db.status === DELIVERY_STATUS.AVAILABLE);
    const busy = active.filter(db => db.status === DELIVERY_STATUS.BUSY);

    const totalDeliveries = deliveryBoys.reduce((sum, db) => sum + (db.totalDeliveries || 0), 0);
    const totalEarnings = deliveryBoys.reduce((sum, db) => sum + (db.totalEarnings || 0), 0);

    const avgRating = deliveryBoys.length > 0
      ? deliveryBoys.reduce((sum, db) => sum + (db.rating || 0), 0) / deliveryBoys.length
      : 0;

    return {
      total: deliveryBoys.length,
      active: active.length,
      online: online.length,
      available: available.length,
      busy: busy.length,
      offline: active.length - online.length,
      activeDeliveries: activeDeliveries.length,
      totalDeliveries,
      totalEarnings,
      avgRating: Math.round(avgRating * 10) / 10
    };
  }, [deliveryBoys, activeDeliveries]);

  const value = {
    deliveryBoys,
    activeDeliveries,
    loading,
    deliveriesLoading,
    stats,
    addDeliveryBoy,
    updateDeliveryBoy,
    deleteDeliveryBoy,
    toggleDeliveryBoyStatus,
    updateOnlineStatus,
    updateLocation,
    assignOrder,
    completeDelivery,
    updateRating,
    getDeliveryBoysByStatus,
    getAvailableDeliveryBoys,
    getActiveDeliveryBoys,
    getDeliveryBoyById,
    getDeliveriesByDeliveryBoy,
    DELIVERY_STATUS
  };

  return (
    <DeliveryContext.Provider value={value}>
      {children}
    </DeliveryContext.Provider>
  );
};
